/**
 * Terms of Service — placeholder content.
 * Public route (/terms), accessible without auth.
 */
import { Link } from 'react-router-dom';
import { routes } from '../../routes.js';

export function Terms() {
  return (
    <div className="min-h-screen bg-canvas font-sans">
      <header className="border-b border-scope-border-soft bg-white px-8 py-5">
        <div className="mx-auto max-w-3xl flex items-center justify-between">
          <Link to={routes.login} className="text-lg font-bold tracking-tight text-scope-navy">
            Scope
          </Link>
          <Link
            to={routes.login}
            className="text-sm text-scope-text-muted hover:text-scope-navy transition"
          >
            ← Back to login
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-8 py-12">
        <div className="mb-8">
          <h1 className="text-3xl font-bold tracking-tight text-scope-navy">Terms of Service</h1>
          <p className="mt-2 text-sm text-scope-text-muted">
            Last updated: May 2026 · Effective date: May 2026
          </p>
        </div>

        <div className="space-y-8 text-sm leading-relaxed">
          <Section title="1. Acceptance of terms">
            <p>
              By accessing or using Scope (&ldquo;the Service&rdquo;), you agree to be bound by
              these Terms of Service and our{' '}
              <Link to={routes.privacy} className="text-scope-navy underline hover:no-underline">
                Privacy Policy
              </Link>
              . If you do not agree, do not use the Service.
            </p>
          </Section>

          <Section title="2. Description of service">
            <p>
              Scope is a project and client management platform for agencies, operated by
              GestureLoop Ltd. The Service allows agencies to manage client engagements,
              deliverables, and reporting, and allows clients to review and approve deliverables via
              a secure portal.
            </p>
          </Section>

          <Section title="3. User accounts">
            <ul className="list-disc space-y-2 pl-5">
              <li>
                You are responsible for maintaining the confidentiality of your account credentials.
              </li>
              <li>You must notify us immediately of any unauthorised use of your account.</li>
              <li>
                Accounts are created by invitation only. You may not share your account with others.
              </li>
            </ul>
          </Section>

          <Section title="4. Acceptable use">
            <p>You agree not to:</p>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              <li>Use the Service for any unlawful purpose.</li>
              <li>
                Attempt to gain unauthorised access to any part of the Service or its
                infrastructure.
              </li>
              <li>Transmit any malicious code, spam, or unsolicited communications.</li>
              <li>Reverse-engineer, decompile, or disassemble any part of the Service.</li>
            </ul>
          </Section>

          <Section title="5. Data and privacy">
            <p>
              Your use of the Service is subject to our{' '}
              <Link to={routes.privacy} className="text-scope-navy underline hover:no-underline">
                Privacy Policy
              </Link>
              , which describes how we collect, use, and store your data. All data is stored within
              the EU. You retain ownership of all content you submit to the Service.
            </p>
          </Section>

          <Section title="6. Intellectual property">
            <p>
              The Service and its original content (excluding user data) are and will remain the
              exclusive property of GestureLoop Ltd. Our trademarks and trade dress may not be used
              without prior written permission.
            </p>
          </Section>

          <Section title="7. Disclaimer of warranties">
            <p>
              The Service is provided &ldquo;as is&rdquo; and &ldquo;as available&rdquo; without any
              warranty of any kind, express or implied. We do not warrant that the Service will be
              uninterrupted, error-free, or free of viruses or other harmful components.
            </p>
          </Section>

          <Section title="8. Limitation of liability">
            <p>
              To the fullest extent permitted by law, GestureLoop Ltd shall not be liable for any
              indirect, incidental, special, consequential, or punitive damages arising from your
              use of the Service.
            </p>
          </Section>

          <Section title="9. Termination">
            <p>
              We may suspend or terminate your access to the Service at any time, with or without
              cause, with or without notice. Upon termination, your right to use the Service ceases
              immediately.
            </p>
          </Section>

          <Section title="10. Governing law">
            <p>
              These Terms are governed by the laws of Ireland. Any disputes shall be subject to the
              exclusive jurisdiction of the Irish courts.
            </p>
          </Section>

          <Section title="11. Contact">
            <p>
              Questions about these Terms? Email{' '}
              <a
                href="mailto:privacy@scope.gestureloop.com"
                className="text-scope-navy underline hover:no-underline"
              >
                privacy@scope.gestureloop.com
              </a>
              .
            </p>
          </Section>
        </div>

        <div className="mt-12 flex items-center justify-between border-t border-scope-border-soft pt-6 text-sm text-scope-text-muted">
          <span>GestureLoop Ltd &mdash; Scope v1</span>
          <Link to={routes.privacy} className="hover:text-scope-navy">
            ← Privacy Policy
          </Link>
        </div>
      </main>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="mb-3 text-base font-semibold text-scope-navy">{title}</h2>
      <div className="text-scope-text-muted">{children}</div>
    </section>
  );
}
